import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Drawer, Empty, InputNumber, Space, Spin, Table, Tag, Tooltip, message } from "antd";
import { CaretDownOutlined, CaretRightOutlined, LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fmtQty } from "../executionNorm";
import {
  DaySheetScope,
  DaySheetTask,
  NormActualLine,
  NormActualSheet,
  normaliseNormActuals,
  siteDailyApis,
} from "../../../../api/SiteDaily";
import DaySheetTree from "./DaySheetTree";
import PendingByDateList from "./PendingByDateList";
import { fromInput, inputValue, isPresent } from "./daySheet";
import CivilTaskFilterBar from "./CivilTaskFilterBar";
import {
  HEAD_LABEL,
  NormDraft,
  TaskNormDrafts,
  aboveNorm,
  anyDirty,
  draftsOf,
  isDirty as lineDirty,
  normKey,
  saveRowsOf,
  totalQtyOf,
  totalsOf,
} from "./normLines";
import {
  SheetSectionHandle,
  SheetSectionMeta,
  saveSequentially,
  sheetMeta,
  useDaySheet,
  filterCivilSheet,
} from "./daySheet";

// Mucking & PRW — the two OUTSOURCED heads of the client's ABS cost abstract.
// Mucking is the haulage of muck to a dump; PRW is the sub-contracted works
// line. Both have a norm applied to the task, which sets the day's target at
// that norm's quantity per unit of output times what the task produced; this
// screen captures what was actually spent.
//
// Until now neither head was captured anywhere: project-progress took actual =
// target on every producing day, so they reported zero deviation forever.
//
// The task tree is the MATERIALS day sheet (producedOnly), deliberately: you
// only haul muck for work you actually did, so the set of tasks that matters is
// the set that produced. A task with no Mucking or PRW norm applied opens to a
// clear "nothing applied" message rather than an empty grid. A dedicated list
// filtered to tasks with these norms applied would be tighter and is the
// natural follow-up.

interface Props {
  projectId: number;
  date: string; // 'YYYY-MM-DD'
  scope: DaySheetScope;
  phaseId?: number;
  activityId?: number;
  reloadToken: number;
  onSheetChange: (meta: SheetSectionMeta) => void;
  onDirtyChange: (count: number) => void;
}

const MuckingPrwSection = forwardRef<SheetSectionHandle, Props>(
  ({ projectId, date, scope, phaseId, activityId, reloadToken, onSheetChange, onDirtyChange }, ref) => {
    // Booked against civil-completed TASKS, not a day (see Material-Stores).
    const { sheet, loading, error } = useDaySheet(
      "materials",
      projectId,
      { date, scope, phaseId, activityId, civilDone: true },
      reloadToken
    );
    const locked = !!sheet?.dayLocked;
    // Civil-done basis: default to tasks still owed Mucking / PRW, or every civil-done
    // task for extra / corrective entries; plus a search.
    const [pendingOnly, setPendingOnly] = useState(true);
    const [taskSearch, setTaskSearch] = useState("");
    // Owed Mucking / PRW, or a civil date today's entry booked (listed so it
    // can be corrected) — both keep the task in "Pending Mucking / PRW".
    const pendingOf = useCallback(
      (task: DaySheetTask) => task.pendingCostHeadCount || (task.pendingCostHeadDates.length > 0 ? 1 : 0),
      []
    );
    const shownSheet = useMemo(
      () => filterCivilSheet(sheet, { pendingOnly, search: taskSearch, pendingOf }),
      [sheet, pendingOnly, taskSearch, pendingOf]
    );
    const civilCounts = useMemo(() => {
      const all = (sheet?.phases ?? []).flatMap((p) => p.activities).flatMap((a) => a.tasks);
      return { all: all.length, pending: all.filter((t) => pendingOf(t) > 0).length };
    }, [sheet, pendingOf]);

    // A task opens in a DRAWER (as on Material-Stores and Mechanical). The
    // order it walks is frozen on open, so a save that drops a task from the
    // pending list never makes "next" skip one.
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    // A stop is a task, or a task ON a civil date when opened from the
    // date-wise Pending list (the drawer then opens with that date first).
    const [navOrder, setNavOrder] = useState<Array<{ taskId: number; date: string | null }>>([]);
    const [focusDate, setFocusDate] = useState<string | null>(null);
    // Civil dates opened / closed by hand per task-date view; unset = open, or
    // only the focus date open when the drawer came from a date.
    const [dayOpen, setDayOpen] = useState<Record<string, boolean>>({});
    const [sheets, setSheets] = useState<Record<number, NormActualSheet | null>>({});
    const [drafts, setDrafts] = useState<Record<number, TaskNormDrafts>>({});
    const [busy, setBusy] = useState<Record<number, boolean>>({});
    // The save loop must read the LATEST drafts, not the ones its closure was
    // born with — one task's refetch must not hand the next task stale lines.
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;
    const sheetsRef = useRef(sheets);
    sheetsRef.current = sheets;

    useEffect(() => onSheetChange(sheetMeta(sheet, loading)), [sheet, loading, onSheetChange]);

    const dirtyCount = Object.keys(sheetsRef.current).filter((taskId) =>
      anyDirty(sheets[Number(taskId)], drafts[Number(taskId)] ?? {})
    ).length;
    useEffect(() => onDirtyChange(dirtyCount), [dirtyCount, onDirtyChange]);

    // A date or scope change invalidates every loaded task-day.
    useEffect(() => {
      setSheets({});
      setDrafts({});
      setOpenTaskId(null);
    }, [date, projectId, scope, phaseId, activityId, reloadToken]);

    const load = useCallback(
      async (taskId: number) => {
        setBusy((prev) => ({ ...prev, [taskId]: true }));
        try {
          const res = await siteDailyApis.costHeads(taskId, { date });
          if (res?.status !== true) {
            message.error(res?.message || "Could not load Mucking / PRW for this task");
            return;
          }
          const next = normaliseNormActuals(res.data);
          setSheets((prev) => ({ ...prev, [taskId]: next }));
          setDrafts((prev) => ({ ...prev, [taskId]: draftsOf(next) }));
        } finally {
          setBusy((prev) => ({ ...prev, [taskId]: false }));
        }
      },
      [date]
    );

    // Lazy: a task's norm lines load the first time it is opened, and stay
    // (with their draft) while the drawer moves on or closes.
    const showTask = (taskId: number) => {
      setOpenTaskId(taskId);
      if (sheetsRef.current[taskId] === undefined) load(taskId);
    };
    const shownOrder = useMemo(
      () => (shownSheet?.phases || []).flatMap((p) => p.activities.flatMap((a) => a.tasks.map((t) => t.taskId))),
      [shownSheet]
    );
    const tasksById = useMemo(() => {
      const map = new Map<number, DaySheetTask>();
      (sheet?.phases || []).forEach((p) => p.activities.forEach((a) => a.tasks.forEach((t) => map.set(t.taskId, t))));
      return map;
    }, [sheet]);
    const showStop = (stop: { taskId: number; date: string | null }) => {
      setFocusDate(stop.date);
      setDayOpen({});
      showTask(stop.taskId);
    };
    const openFromList = (task: DaySheetTask) => {
      setNavOrder(shownOrder.map((taskId) => ({ taskId, date: null })));
      showStop({ taskId: task.taskId, date: null });
    };
    // The date-wise list's order: civil date by civil date, oldest first.
    const dateOrder = useMemo(() => {
      const stops: Array<{ taskId: number; date: string | null }> = [];
      (shownSheet?.phases || []).forEach((p) =>
        p.activities.forEach((a) =>
          a.tasks.forEach((t) => t.pendingCostHeadDates.forEach((d) => stops.push({ taskId: t.taskId, date: d.workDate })))
        )
      );
      return stops.sort((x, y) => (x.date as string).localeCompare(y.date as string));
    }, [shownSheet]);
    const openFromDate = (task: DaySheetTask, workDate: string) => {
      setNavOrder(dateOrder);
      showStop({ taskId: task.taskId, date: workDate });
    };

    const saveOne = useCallback(
      async (taskId: number): Promise<boolean> => {
        const taskSheet = sheetsRef.current[taskId];
        if (!taskSheet) return true;
        const rows = saveRowsOf(taskSheet, draftsRef.current[taskId] ?? {});
        setBusy((prev) => ({ ...prev, [taskId]: true }));
        try {
          const res = await siteDailyApis.saveCostHeads({ taskId, actualDate: date, rows, byCivilDate: true });
          if (res?.status !== true) {
            message.error(res?.message || `Could not save task ${taskId}`);
            return false;
          }
          if (res.data?.warning) message.warning(res.data.warning);
          await load(taskId);
          return true;
        } catch {
          return false;
        } finally {
          setBusy((prev) => ({ ...prev, [taskId]: false }));
        }
      },
      [date, load]
    );

    useImperativeHandle(ref, () => ({
      saveAll: async () => {
        const dirty = Object.keys(sheetsRef.current)
          .map(Number)
          .filter((taskId) => anyDirty(sheetsRef.current[taskId], draftsRef.current[taskId] ?? {}))
          .map((taskId) => ({ id: taskId, label: sheetsRef.current[taskId]?.taskSequenceId || `Task ${taskId}` }));
        if (!dirty.length) {
          message.info("Nothing to save");
          return;
        }
        await saveSequentially(dirty, saveOne);
      },
      discardAll: () => {
        setDrafts(() => {
          const next: Record<number, TaskNormDrafts> = {};
          Object.entries(sheetsRef.current).forEach(([taskId, taskSheet]) => {
            next[Number(taskId)] = draftsOf(taskSheet);
          });
          return next;
        });
        setOpenTaskId(null);
      },
    }));

    // ---- the task drawer ----------------------------------------------------
    const openTask = openTaskId !== null ? tasksById.get(openTaskId) : undefined;
    const openSheet = openTaskId !== null ? sheets[openTaskId] : undefined;
    const openDirty = openTaskId !== null && anyDirty(openSheet ?? null, drafts[openTaskId] ?? {});
    const openBusy = openTaskId !== null && !!busy[openTaskId];
    const navIndex =
      openTaskId !== null ? navOrder.findIndex((stop) => stop.taskId === openTaskId && stop.date === focusDate) : -1;
    const prevStop = navIndex > 0 ? navOrder[navIndex - 1] : null;
    const nextStop = navIndex >= 0 && navIndex < navOrder.length - 1 ? navOrder[navIndex + 1] : null;
    const resetTask = (taskId: number) =>
      setDrafts((prev) => ({ ...prev, [taskId]: draftsOf(sheetsRef.current[taskId] ?? null) }));
    const saveOpen = async (): Promise<boolean> => {
      if (openTaskId === null) return false;
      const ok = await saveOne(openTaskId);
      if (ok) message.success("Mucking / PRW saved");
      return ok;
    };
    const saveAndNext = async () => {
      // The next task is fixed BEFORE the save — the save may reorder the list.
      const target = nextStop;
      if ((await saveOpen()) && target !== null) showStop(target);
    };

    const setDraft = (taskId: number, key: string, patch: Partial<NormDraft>) =>
      setDrafts((prev) => {
        const blank: NormDraft = { qty: "", rate: "", remarks: "", pendingDays: {} };
        const current = prev[taskId]?.[key] ?? blank;
        return {
          ...prev,
          [taskId]: { ...(prev[taskId] ?? {}), [key]: { ...current, ...patch } },
        };
      });


    // ---- the task drawer, by CIVIL DATE ------------------------------------
    // Each civil date with the norm lines it needs: Target qty / rate / amount
    // (the norm for that day's output) and the Actual boxes — the date's whole
    // booking, earlier entries plus today's share. Only today's share is saved,
    // against that civil date, and booked there exactly. The clicked date comes
    // first and open, the others collapsed beneath it.
    const EPS = 0.00005;
    const num = (v: string) => (isPresent(v) ? Number(v) : 0);
    const q4 = (v: number) => String(Math.round(v * 10000) / 10000);
    const fmt = (v: number | string) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 });
    const rupees = (v: number | null) => (v === null ? "—" : `₹ ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
    type Day = NormActualLine["civilDays"][number];
    const earlierQtyOf = (d: Day) => Math.max(0, num(d.bookedQty) - num(d.bookedTodayQty));
    const roomOf = (d: Day) => Math.max(0, num(d.requiredQty) - earlierQtyOf(d));
    const liveDay = (d: Day) => num(d.pendingQty) > 0 || num(d.bookedTodayQty) > 0;

    const renderTaskDetail = (task: DaySheetTask) => {
      const taskSheet = sheets[task.taskId];
      if (busy[task.taskId] && !taskSheet) {
        return (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin />
          </div>
        );
      }
      if (!taskSheet) return null;
      if (!taskSheet.lines.length) {
        return (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No Mucking or PRW norm is applied to this task — apply one to capture against it"
          />
        );
      }
      const taskDrafts = drafts[task.taskId] ?? {};
      const draftFor = (line: NormActualLine): NormDraft =>
        taskDrafts[normKey(line)] ?? { qty: "", rate: "", remarks: "", pendingDays: {} };
      const setDayQty = (line: NormActualLine, workDate: string, qty: string) =>
        setDraft(task.taskId, normKey(line), { pendingDays: { ...draftFor(line).pendingDays, [workDate]: qty } });
      // Money earlier entries booked on a date (each at its own rate).
      const earlierMoney = (line: NormActualLine, d: Day) => {
        if (d.bookedAmount === "") return { amount: null as number | null, rate: null as number | null };
        const savedShare = num(d.bookedTodayQty);
        const savedRate = isPresent(line.actualRate) ? Number(line.actualRate) : null;
        const amount = num(d.bookedAmount) - (savedRate === null ? 0 : savedShare * savedRate);
        const priced = num(d.bookedPricedQty) - (savedRate === null ? 0 : savedShare);
        return priced <= EPS ? { amount: null, rate: null } : { amount: Math.round(amount * 100) / 100, rate: amount / priced };
      };

      type Row =
        | { kind: "day"; key: string; workDate: string; producedQty: string; open: boolean; count: number }
        | { kind: "line"; key: string; line: NormActualLine; day: Day }
        | { kind: "otherHead"; key: string }
        | { kind: "other"; key: string; line: NormActualLine };
      const rows: Row[] = [];
      const byDate = new Map<string, { producedQty: string; items: Array<{ line: NormActualLine; day: Day }> }>();
      taskSheet.lines.forEach((line) =>
        line.civilDays
          .filter((d) => liveDay(d) || (focusDate !== null && d.workDate === focusDate && num(d.requiredQty) > 0))
          .forEach((d) => {
            const slot = byDate.get(d.workDate) ?? { producedQty: d.producedQty, items: [] };
            slot.items.push({ line, day: d });
            byDate.set(d.workDate, slot);
          })
      );
      Array.from(byDate.keys())
        .sort((x, y) => (x === focusDate ? -1 : y === focusDate ? 1 : x.localeCompare(y)))
        .forEach((workDate) => {
          const slot = byDate.get(workDate)!;
          const key = `D-${workDate}`;
          const open = dayOpen[key] ?? (!focusDate || workDate === focusDate);
          rows.push({ kind: "day", key, workDate, producedQty: slot.producedQty, open, count: slot.items.length });
          if (open) slot.items.forEach(({ line, day }) => rows.push({ kind: "line", key: `${workDate}-${normKey(line)}`, line, day }));
        });
      const listed = new Set<string>();
      byDate.forEach((slot) => slot.items.forEach(({ line }) => listed.add(normKey(line))));
      const others = taskSheet.lines.filter((line) => !listed.has(normKey(line)) && isPresent(draftFor(line).qty));
      if (others.length) {
        rows.push({ kind: "otherHead", key: "OTHER" });
        others.forEach((line) => rows.push({ kind: "other", key: `O-${normKey(line)}`, line }));
      }

      const span = (row: Row, key: string) =>
        row.kind === "day" || row.kind === "otherHead"
          ? key === "head"
            ? { className: "pms-mat-group-cell" }
            : key === "tQty"
              ? { colSpan: 6, className: "pms-mat-group-cell" }
              : { colSpan: 0 }
          : {};
      const lineCell = (line: NormActualLine) => (
        <span style={{ paddingLeft: 18, display: "inline-block" }}>
          <b>{HEAD_LABEL[line.head] || line.head}</b>
          <div className="pms-norm-sub">
            {line.itemCode}
            {line.itemName ? ` · ${line.itemName}` : ""}
          </div>
          {line.nearestDump && (
            <div className="pms-norm-sub">
              {line.nearestDump}
              {line.totalLead ? ` · lead ${line.totalLead} km` : ""}
            </div>
          )}
        </span>
      );
      const columns = [
        {
          title: "Civil date / Head",
          key: "head",
          width: 280,
          onCell: (row: Row) => span(row, "head"),
          render: (_: any, row: Row) => {
            if (row.kind === "day") {
              return (
                <span className="pms-mat-group-name" onClick={() => setDayOpen((cur) => ({ ...cur, [row.key]: !row.open }))}>
                  {row.open ? <CaretDownOutlined /> : <CaretRightOutlined />}
                  <span style={{ color: "#022c57", fontWeight: 600 }}>{dayjs(row.workDate).format("DD-MMM-YYYY")}</span>
                  {row.workDate === focusDate && <span className="pms-exec-count-badge">opened</span>}
                  {row.producedQty !== "" && (
                    <span className="pms-exec-group-count">
                      produced {fmt(row.producedQty)} {taskSheet.workUomName}
                    </span>
                  )}
                </span>
              );
            }
            if (row.kind === "otherHead") {
              return (
                <span className="pms-mat-group-name">
                  <span style={{ color: "#022c57", fontWeight: 600 }}>Not against a civil date</span>
                  <span className="pms-exec-group-count">today's entry</span>
                </span>
              );
            }
            return lineCell(row.line);
          },
        },
        {
          title: "Target qty",
          key: "tQty",
          width: 120,
          align: "right" as const,
          onCell: (row: Row) => span(row, "tQty"),
          render: (_: any, row: Row) => {
            if (row.kind === "day") {
              const owing = rows.filter(
                (r): r is Extract<Row, { kind: "line" }> =>
                  r.kind === "line" && r.day.workDate === row.workDate && num(draftFor(r.line).pendingDays[row.workDate] ?? "") + EPS < roomOf(r.day)
              );
              return (
                <span className="pms-mat-day-actions" style={{ justifyContent: "flex-end", width: "100%" }}>
                  <span className="pms-exec-hint">
                    {row.count} line{row.count === 1 ? "" : "s"} · {owing.length} owed
                  </span>
                  {!locked && owing.length > 0 && (
                    <Button size="small" onClick={(e) => { e.stopPropagation(); owing.forEach((r) => setDayQty(r.line, row.workDate, q4(roomOf(r.day)))); }}>
                      Fill this date
                    </Button>
                  )}
                </span>
              );
            }
            if (row.kind === "otherHead") return <span className="pms-exec-hint">booked on its own day, else the oldest civil date first</span>;
            if (row.kind === "other") return <span style={{ color: "#8094ae" }}>—</span>;
            return (
              <span className="exec-num">
                {fmt(row.day.requiredQty)} <span style={{ color: "#8094ae" }}>{row.line.uomName}</span>
              </span>
            );
          },
        },
        {
          title: "Target rate",
          key: "tRate",
          width: 110,
          align: "right" as const,
          onCell: (row: Row) => span(row, "tRate"),
          render: (_: any, row: Row) =>
            row.kind === "line" || row.kind === "other" ? (
              <span className="exec-num">{row.line.targetRate === "" ? "—" : fmt(row.line.targetRate)}</span>
            ) : null,
        },
        {
          title: "Target amount",
          key: "tAmount",
          width: 130,
          align: "right" as const,
          onCell: (row: Row) => span(row, "tAmount"),
          render: (_: any, row: Row) =>
            row.kind === "line" && row.line.targetRate !== "" ? (
              <span className="exec-num">{rupees(Math.round(num(row.day.requiredQty) * num(row.line.targetRate) * 100) / 100)}</span>
            ) : row.kind === "line" || row.kind === "other" ? (
              <span style={{ color: "#8094ae" }}>—</span>
            ) : null,
        },
        {
          title: "Actual qty",
          key: "qty",
          width: 180,
          onCell: (row: Row) => span(row, "qty"),
          render: (_: any, row: Row) => {
            if (row.kind === "other") {
              const d = draftFor(row.line);
              return (
                <InputNumber
                  stringMode
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={inputValue(d.qty)}
                  disabled={locked}
                  onChange={(v) => setDraft(task.taskId, normKey(row.line), { qty: fromInput(v) })}
                  style={{ width: "100%" }}
                />
              );
            }
            if (row.kind !== "line") return null;
            const { line, day } = row;
            const today = draftFor(line).pendingDays[day.workDate] ?? "";
            const earlier = earlierQtyOf(day);
            const required = num(day.requiredQty);
            const shown = isPresent(today) ? q4(earlier + num(today)) : earlier > EPS ? q4(earlier) : "";
            const room = roomOf(day);
            return (
              <div>
                <Tooltip title={earlier > EPS ? `${fmt(q4(earlier))} ${line.uomName} of this was booked by an earlier entry` : ""}>
                  <InputNumber
                    stringMode
                    min={q4(earlier)}
                    step="0.01"
                    placeholder="0"
                    value={inputValue(shown)}
                    disabled={locked}
                    onChange={(v) => {
                      const total = fromInput(v);
                      if (total === "") return setDayQty(line, day.workDate, "");
                      const share = num(total) - earlier;
                      setDayQty(line, day.workDate, share > EPS ? q4(share) : "");
                    }}
                    style={{ width: "100%" }}
                  />
                </Tooltip>
                {room > EPS && num(today) + EPS < room && !locked && (
                  <div className="pms-exec-hint">
                    <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => setDayQty(line, day.workDate, q4(room))}>
                      use {fmt(q4(required))}
                    </Button>
                  </div>
                )}
                {num(shown) > required + EPS && (
                  <div className="pms-exec-hint pms-exec-hint--amber">over target by {fmt(q4(num(shown) - required))}</div>
                )}
              </div>
            );
          },
        },
        {
          title: "Actual rate",
          key: "rate",
          width: 140,
          onCell: (row: Row) => span(row, "rate"),
          render: (_: any, row: Row) => {
            if (row.kind !== "line" && row.kind !== "other") return null;
            const d = draftFor(row.line);
            const earlier = row.kind === "line" ? earlierMoney(row.line, row.day) : { amount: null, rate: null };
            const over = aboveNorm(row.line, d);
            return (
              <div>
                <Tooltip
                  title={
                    earlier.rate !== null
                      ? isPresent(d.rate)
                        ? `Today's rate. The earlier entry was at ${fmt(earlier.rate.toFixed(2))}`
                        : "Rate of the earlier entry — type to set today's"
                      : "One rate per line for today's whole entry"
                  }
                >
                  <InputNumber
                    stringMode
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={inputValue(isPresent(d.rate) || earlier.rate === null ? d.rate : String(Math.round(earlier.rate * 100) / 100))}
                    disabled={locked}
                    status={over ? "warning" : undefined}
                    onChange={(v) => setDraft(task.taskId, normKey(row.line), { rate: fromInput(v) })}
                    style={{ width: "100%" }}
                  />
                </Tooltip>
                {over && <div className="pms-norm-over">above the norm {row.line.targetRate}</div>}
              </div>
            );
          },
        },
        {
          title: "Actual amount",
          key: "amount",
          width: 140,
          align: "right" as const,
          onCell: (row: Row) => span(row, "amount"),
          render: (_: any, row: Row) => {
            if (row.kind === "other") {
              const d = draftFor(row.line);
              const v = isPresent(d.qty) && isPresent(d.rate) ? Math.round(num(d.qty) * num(d.rate) * 100) / 100 : null;
              return <span className="exec-num" style={{ fontWeight: 600 }}>{rupees(v)}</span>;
            }
            if (row.kind !== "line") return null;
            const d = draftFor(row.line);
            const earlier = earlierMoney(row.line, row.day).amount;
            const share = d.pendingDays[row.day.workDate] ?? "";
            const today = isPresent(share) && isPresent(d.rate) ? num(share) * num(d.rate) : null;
            const total = earlier === null && today === null ? null : Math.round(((earlier ?? 0) + (today ?? 0)) * 100) / 100;
            return <span className="exec-num" style={{ fontWeight: 600 }}>{rupees(total)}</span>;
          },
        },
      ];
      const totals = totalsOf(taskSheet, taskDrafts);
      return (
        <div className="pms-norm-block">
          {focusDate && (
            <div className="pms-exec-hint" style={{ marginBottom: 6 }}>
              Opened from <b style={{ color: "#364a63" }}>{dayjs(focusDate).format("DD-MMM-YYYY")}</b> — the other civil dates still
              owed are below it; click a date to open it.
            </div>
          )}
          <Table
            size="small"
            rowKey="key"
            className="pms-exec-heads-table pms-mat-table"
            pagination={false}
            dataSource={rows}
            columns={columns as any}
            rowClassName={(row: Row) => (row.kind === "day" || row.kind === "otherHead" ? "pms-mat-group-row" : "")}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No Mucking or PRW owed to earlier civil work"
                />
              ),
            }}
          />
          {totals.actualAmount !== "" && (
            <div className="pms-exec-hint" style={{ marginTop: 8 }}>
              Today's entry: ₹ {Number(totals.actualAmount).toLocaleString("en-IN")}
              {Object.keys(taskDrafts).some((k) => {
                const line = taskSheet.lines.find((l) => normKey(l) === k);
                return line ? lineDirty(line, taskDrafts[k]) : false;
              }) && <Tag color="orange" style={{ marginLeft: 8 }}>unsaved</Tag>}
              {" · "}qty logged today {taskSheet.lines.reduce((sum, l) => sum + num(totalQtyOf(taskDrafts[normKey(l)])), 0).toLocaleString("en-IN")}
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        {locked && (
          <Alert
            type="info"
            showIcon
            message="This day is submitted for approval — Mucking and PRW are read-only until it is reopened."
            style={{ marginBottom: 12 }}
          />
        )}
        <CivilTaskFilterBar
          pendingOnly={pendingOnly}
          onPendingOnlyChange={setPendingOnly}
          search={taskSearch}
          onSearchChange={setTaskSearch}
          pendingCount={civilCounts.pending}
          allCount={civilCounts.all}
          what="Mucking / PRW"
        />
        {pendingOnly ? (
          // Pending Mucking / PRW, DATE-WISE: civil date → activity → task.
          <PendingByDateList
            sheet={shownSheet}
            loading={loading}
            activeTaskId={openTaskId}
            activeDate={focusDate}
            onOpen={openFromDate}
            datesOf={(task) => task.pendingCostHeadDates}
            lineNoun={["norm line", "norm lines"]}
            owedTitle="Lines owed"
            taskState={(task) => {
              const taskSheet = sheets[task.taskId];
              return taskSheet ? { dirty: anyDirty(taskSheet, drafts[task.taskId] ?? {}), saving: !!busy[task.taskId] } : undefined;
            }}
            emptyDescription="Nothing owed — every civil date has its Mucking and PRW booked"
          />
        ) : (
          <DaySheetTree
            sheet={shownSheet}
            pendingCountOf={pendingOf}
            pendingLabel="Mucking / PRW"
            loading={loading}
            expandedTaskIds={[]}
            onExpandedTaskIdsChange={() => undefined}
            renderTaskDetail={() => null}
            onTaskOpen={openFromList}
            activeTaskId={openTaskId}
            taskState={(task) => {
              const taskSheet = sheets[task.taskId];
              if (!taskSheet) return busy[task.taskId] ? { saving: true } : undefined;
              const totals = totalsOf(taskSheet, drafts[task.taskId] ?? {});
              return {
                dirty: anyDirty(taskSheet, drafts[task.taskId] ?? {}),
                saving: !!busy[task.taskId],
                // The day's outsourced cost for this task, so the tree row reads
                // like the other sections' rows.
                amount: totals.actualAmount === "" ? null : Number(totals.actualAmount),
              };
            }}
            emptyDescription="No task produced on this day, so there is nothing to haul or sub-contract against"
            showTrackedTag={false}
          />
        )}
        <Drawer
          open={openTaskId !== null}
          onClose={() => setOpenTaskId(null)}
          width="min(1180px, 94vw)"
          className="pms-mat-drawer"
          title={
            openTask ? (
              <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                <span>
                  <span className="pms-exec-task-seq">{openTask.taskSequenceId}</span>
                  <span className="pms-exec-task-name">{openTask.taskName}</span>
                </span>
                <span className="pms-exec-hint" style={{ fontWeight: 400 }}>
                  Mucking & PRW · {dayjs(date).format("DD-MMM-YYYY")}
                  {openTask.workUomName && openTask.producedTillDate !== ""
                    ? ` · civil done ${fmtQty(openTask.producedTillDate)} ${openTask.workUomName}`
                    : ""}
                </span>
              </span>
            ) : (
              "Mucking & PRW"
            )
          }
          extra={
            navOrder.length > 1 && navIndex >= 0 ? (
              <Space size={4}>
                <Button size="small" icon={<LeftOutlined />} disabled={prevStop === null} onClick={() => prevStop && showStop(prevStop)} aria-label="Previous task" />
                <span className="pms-exec-hint exec-num">
                  {navIndex + 1} / {navOrder.length}
                </span>
                <Button size="small" icon={<RightOutlined />} disabled={nextStop === null} onClick={() => nextStop && showStop(nextStop)} aria-label="Next task" />
              </Space>
            ) : null
          }
          footer={
            openTaskId === null ? null : (
              <div className="pms-mat-drawer-foot">
                <span className="pms-exec-hint">
                  {locked
                    ? "Day submitted — read-only."
                    : openDirty
                      ? "Unsaved changes stay with this task if you move on — save them here or with Save all."
                      : "Actual is what was spent; target is the applied norm for the work."}
                </span>
                {!locked && (
                  <Space>
                    <Button onClick={() => resetTask(openTaskId)} disabled={!openDirty || openBusy}>
                      Reset
                    </Button>
                    <Button icon={<SaveOutlined />} onClick={saveOpen} disabled={!openDirty} loading={openBusy}>
                      Save
                    </Button>
                    {nextStop !== null && (
                      <Button
                        type="primary"
                        onClick={openDirty ? saveAndNext : () => showStop(nextStop)}
                        loading={openBusy}
                      >
                        {openDirty ? "Save & next" : "Next task"} <RightOutlined />
                      </Button>
                    )}
                  </Space>
                )}
              </div>
            )
          }
        >
          {openTask && renderTaskDetail(openTask)}
        </Drawer>
      </>
    );
  }
);

MuckingPrwSection.displayName = "MuckingPrwSection";

export default MuckingPrwSection;
